import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/material";
import Swal from "sweetalert2";

import { currentTenant, userid } from '../../Common/Credentials.js';
import { spin } from '../../Common/NewLoader.jsx';
import { appmasterid } from '../../Common/Permissions.js';
import Header from '../../Component/Header.js';
import Menu from '../../Component/Menu.js';
import Footer from '../../Component/Footer.js';
import UomServices from './UomServices.js';
import { TextField, MenuItem } from "@mui/material";

import DeleteIcon from "../../images/Icons/delete.png";
import Breadcrumb from "../../NewUI/NewComponents/Breadcrumb.jsx";

const UomDataTable = () => {
  const [uom, setUom] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const navigate = useNavigate();

  // Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part)
        ? React.createElement('mark', {
          key: index,
          className: 'UI-search-highlight'
        }, part)
        : part
    );
  };

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  const refreshUom = useCallback(() => {
    setLoading(true);
    UomServices.getUOM(currentTenant)
      .then((response) => {
        const sortedData = response.data.sort((a, b) => a.name?.localeCompare(b.name));
        setUom(sortedData);
        setFilteredData(sortedData);
        setLoading(false);
      })
      .catch((e) => {
        if (e.response?.data?.error === "Unauthorized") {
          Swal.fire({
            title: "Your Session is Lost: Please login Back to resume",
            text: ".",
            icon: "error",
            confirmButtonText: "Ok",
            confirmButtonColor: "#152f5f",
          }).then(() => navigate("/"));
        }
        setLoading(false);
      });
  }, [currentTenant, navigate]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    if (!userid) {
      window.confirm("Your Session is Lost: Please login Back to resume");
      navigate("/");
      return;
    }

    refreshUom();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [navigate, refreshUom]);

  useEffect(() => {
    updateFilteredData();
  }, [uom, searchTerm]);

  // Sorting function
  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'description':
          aValue = a.description || '';
          bValue = b.description || '';
          break;
        default:
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const updateFilteredData = () => {
    let filtered;
    if (!searchTerm.trim()) {
      filtered = uom;
    } else {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = uom.filter(item =>
        item.name?.toLowerCase().includes(lowerSearchTerm) ||
        item.description?.toLowerCase().includes(lowerSearchTerm)
      );
    }
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  // Get current page data for pagination
  const getCurrentPageData = () => {
    const sortedData = getSortedData(filteredData);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedData.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const deleteUom = (id, uomName) => {
    Swal.fire({
      title: `Delete ${uomName}?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        UomServices.deleteUom(id)
          .then(() => {
            Swal.fire({
              title: "Deleted!",
              text: "UOM has been deleted.",
              icon: "success",
              confirmButtonText: "Ok",
              confirmButtonColor: "#152f5f",
            }).then(() => {
              setLoading(true);
              refreshUom();
              setMessage("UOM Deleted Successfully");
            });
          })
          .catch((e) => {
            if (e.response?.data?.error === "Unauthorized") {
              Swal.fire({
                title: "Your Session is Lost: Please login Back to resume",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              }).then(() => navigate("/"));
            } else {
              Swal.fire({
                title: "Error occurred while deleting",
                text: ".",
                icon: "error",
                confirmButtonText: "Ok",
                confirmButtonColor: "#152f5f",
              });
            }
          });
      }
    });
  };

// Update the renderPagination function in UomDataTable.jsx
const renderPagination = () => {
  if (filteredData.length <= itemsPerPage) return null;

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  return (
    <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
      <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} UOMs
      </div>
      <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
        <TextField
          select
          value={itemsPerPage}
          onChange={handleItemsPerPageChange}
          variant="outlined"
          size="small"
          className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
        >
          <MenuItem value={5}>5 per page</MenuItem>
          <MenuItem value={10}>10 per page</MenuItem>
          <MenuItem value={20}>20 per page</MenuItem>
          <MenuItem value={50}>50 per page</MenuItem>
        </TextField>

        <button
          className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
          onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
          onMouseLeave={() => setHovered(null)}
        >
          {isMobile ? 'First' : 'First'}
        </button>

        <button
          className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && currentPage !== 1 ? 'UI-pagination-button-hover' : ''}`}
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
          onMouseLeave={() => setHovered(null)}
        >
          {isMobile ? 'Prev' : 'Previous'}
        </button>

        <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Page {currentPage} of {totalPages}
        </span>

        <button
          className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
          onMouseLeave={() => setHovered(null)}
        >
          {isMobile ? 'Next' : 'Next'}
        </button>

        <button
          className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && currentPage !== totalPages ? 'UI-pagination-button-hover' : ''}`}
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
          onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
          onMouseLeave={() => setHovered(null)}
        >
          {isMobile ? 'Last' : 'Last'}
        </button>
      </div>
    </div>
  );
};

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  const renderTable = () => {
    const currentPageData = getCurrentPageData();

    if (filteredData.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTerm ? 'No UOMs found matching your search' : 'No UOMs available'}
        </div>
      );
    }

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`} style={{ tableLayout: 'fixed' }}>
            <thead className="UI-table-header">
              <tr>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'name' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('name')}
                  style={{ cursor: 'pointer', width: isMobile ? '40%' : '40%' }}
                >
                  <div className="UI-th-content">
                    UOM
                    <SortIndicator columnKey="name" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'description' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('description')}
                  style={{ cursor: 'pointer', width: isMobile ? '40%' : '50%' }}
                >
                  <div className="UI-th-content">
                    Description
                    <SortIndicator columnKey="description" />
                  </div>
                </th>
                {appmasterid === "2" || appmasterid === "3" ? (
                  <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`} style={{ width: isMobile ? '20%' : '10%' }}>
                    Actions
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((item) => (
                <tr
                  key={item.id}
                  className={`UI-table-row ${hoveredRow === item.id ? 'UI-table-row-hovered' : ''} ${isMobile ? 'UI-table-row-mobile' : ''}`}
                  onMouseEnter={() => setHoveredRow(item.id)}
                  onMouseLeave={() => {
                    setHoveredRow(null);
                    setHoveredAction(null);
                  }}
                >
                  {/* UOM Name */}
                  <td
                    className={`UI-td UI-service-name ${hoveredRow === item.id ? 'UI-service-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                    style={{ width: isMobile ? '40%' : '40%' }}
                  >
                    {highlightSearchText(item.name, searchTerm)}
                  </td>

                  {/* Description */}
                  <td
                    className={`UI-td ${hoveredRow === item.id ? 'UI-item-name-hover' : ''} ${isMobile ? 'UI-td-mobile' : ''}`}
                    style={{ width: isMobile ? '40%' : '50%' }}
                  >
                    {highlightSearchText(item.description, searchTerm)}
                  </td>

                  {/* Actions Cell - Only show if user has permission */}
                  {appmasterid === "2" || appmasterid === "3" ? (
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`} style={{ width: isMobile ? '20%' : '10%' }}>
                      <div className="UI-action-buttons" style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                        {/* Delete UOM - Don't show for tenantid = "ALL" */}
                        {item.tenantid !== "ALL" && (
                          <Tooltip
                            title="Delete UOM"
                            arrow
                            slotProps={{
                              popper: {
                                sx: {
                                  pointerEvents: 'none',
                                },
                              },
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteUom(item.id, item.name);
                              }}
                              className={`UI-action-button ${hoveredAction === `delete-${item.id}` ? 'UI-delete-button-hover' : ''} ${isMobile ? 'UI-action-button-mobile' : ''}`}
                              onMouseEnter={() => setHoveredAction(`delete-${item.id}`)}
                              onMouseLeave={() => setHoveredAction(null)}
                              style={{
                                minWidth: '28px',
                                minHeight: '28px',
                              }}
                            >
                              <img
                                src={DeleteIcon}
                                alt="Delete"
                                className={`UI-action-icon ${isMobile ? 'UI-action-icon-mobile' : ''} ${hoveredAction === `delete-${item.id}` ? 'UI-action-icon-hover' : ''}`}
                                style={{ width: "20px", height: "20px" }}
                              />
                            </button>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </>
    );
  };

  return (
    <div>
      <Header />
      <Menu />
      <div className="content-wrapper">
        <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
            {/* Header */}
            <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
              <Breadcrumb breadcrumbs={[{name:"Home",path:"/inventory"},{name:"Unit of Measurement (UOM)",path:null}]}/>
              <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
                <div>
                  <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
                    Unit of Measurement (UOM)
                  </h1>
                  <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                    Manage unit of measurement records
                  </p>
                </div>

                <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
                  {/* Success Message */}
                  {message && (
                    <div className="text-center text-success text-uppercase mb-2">
                      {message}
                    </div>
                  )}

                  {/* Mobile Search */}
                  {isMobile && (
                    <input
                      type="text"
                      placeholder="Search UOMs..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      className="UI-mobile-search-input"
                    />
                  )}

                  <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                    {/* Desktop Search Toggle */}
                    {!isMobile && (
                      <div
                        className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                        onClick={() => {
                          if (!showSearch) setShowSearch(true);
                        }}
                        onMouseEnter={() => setHovered("search")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i
                          className="fa fa-search UI-search-icon"
                        />
                        {!showSearch && "Search"}
                        {showSearch && (
                          <input
                            type="text"
                            placeholder="Search UOMs..."
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="UI-search-input"
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            onBlur={() => {
                              if (searchTerm.trim() === "") {
                                setShowSearch(false);
                              }
                            }}
                          />
                        )}
                      </div>
                    )}

                    {/* New UOM button */}
                    {(appmasterid === "2" || appmasterid === "3") && (
                      <Link
                        to="/newuom"
                        state={{ id: -1, pagename: 'New UOM' }}
                        className={`UI-btn UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''} ${isMobile ? 'UI-btn-mobile' : ''}`}
                        onMouseEnter={() => setHovered("new")}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <i className="fa fa-plus" /> {isMobile ? "New" : "New UOM"}
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="UI-loading-container">
                {spin}
              </div>
            ) : (
              <div className="UI-form-card">
              {renderTable()}
              </div>
            )}
          </div>
      </div>
      <Footer />
    </div>
  );
};

export default UomDataTable;